import { useEffect, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { Alert, Button, Link, Stack } from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import { useTranslation } from "react-i18next";
import AuthPageShell from "../components/auth/AuthPageShell";
import PasswordField from "../components/auth/PasswordField";
import PasswordStrengthChecklist from "../components/auth/PasswordStrengthChecklist";
import { resetPassword, validateResetToken } from "../api/auth";
import { evaluatePassword } from "../utils/passwordPolicy";

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [tokenError, setTokenError] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const passwordPolicy = evaluatePassword(password);

  useEffect(() => {
    let active = true;

    const validate = async () => {
      if (!token) {
        setTokenError(t("authReset.missingToken"));
        return;
      }

      try {
        const data = await validateResetToken(token);
        if (active && !data.valid) {
          setTokenError(t("authReset.invalidToken"));
        }
      } catch (err) {
        if (active) {
          setTokenError(
            err.response?.data?.error || t("authReset.validateError")
          );
        }
      }
    };

    validate();

    return () => {
      active = false;
    };
  }, [token, t]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (password !== confirmPassword) {
      setError(t("authRegister.passwordMismatch"));
      return;
    }

    if (!passwordPolicy.isValid) {
      setError(t("authRegister.passwordPolicy.error"));
      return;
    }

    setLoading(true);
    try {
      await resetPassword({ password, token });
      setSuccess(t("authReset.success"));
    } catch (err) {
      setError(err.response?.data?.error || t("authReset.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageShell
      title={t("authReset.title")}
      subtitle={t("authReset.subtitle")}
    >
      <Stack component="form" spacing={2} onSubmit={handleSubmit}>
        {tokenError && <Alert severity="error">{tokenError}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        <PasswordField
          label={t("authReset.newPassword")}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          required
          disabled={Boolean(tokenError || success)}
          fullWidth
        />
        <PasswordStrengthChecklist password={password} />
        <PasswordField
          label={t("authReset.confirmPassword")}
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          autoComplete="new-password"
          required
          disabled={Boolean(tokenError || success)}
          fullWidth
        />

        <Button
          type="submit"
          variant="contained"
          startIcon={<LockResetIcon />}
          disabled={
            loading || Boolean(tokenError || success) || !passwordPolicy.isValid
          }
          fullWidth
        >
          {loading ? t("authReset.updating") : t("authReset.submit")}
        </Button>

        <Link component={RouterLink} to="/login" underline="hover">
          {t("authForgot.backToLogin")}
        </Link>
      </Stack>
    </AuthPageShell>
  );
};

export default ResetPasswordPage;
